import { SNAPSHOT_CACHE_KEY } from './constants';
import type { PredictionsRes } from './types';
import type { ShellyReqWrapper } from './types';

/**
 * Static class to interact with the Infinno AI prediction API.
 */
export class AiBreakdownService {
	private static readonly url = import.meta.env.VITE_INFINNO_NILM_API_URL;
	private static readonly headers = {
		'Content-Type': 'application/json',
		Accept: 'application/json',
	};

	/**
	 * Retrieves the authentication token from localStorage
	 * @returns string | null
	 */
	private static getAuthToken(): string | null {
		try {
			const userData = localStorage.getItem('user_data');
			if (!userData) return null;
			const parsed = JSON.parse(userData);
			return parsed.token || null;
		} catch (error) {
			return null;
		}
	}

	/**
	 * Gets headers with Authorization token
	 * @returns Record<string, string>
	 */
	private static getHeaders(): Record<string, string> {
		const headers: Record<string, string> = { ...this.headers };
		const token = this.getAuthToken();
		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}
		return headers;
	}

	/**
	 * Get predictions from infinno's api
	 * @param date - Date string in YYYY-MM-DD format
	 * @returns Promise\<InfinnoChatStatusResponse\>
	 */
	static async getPredictions(date: string): Promise<ShellyReqWrapper<PredictionsRes>> {
		const url = `${this.url}/get-predictions-shelly-app?date=${encodeURIComponent(date)}`;
		const request = new Request(url, {
			method: 'GET',
			headers: this.getHeaders(),
			cache: 'no-cache',
		});

		try {
			const response = await fetch(request);

			if (response.status !== 200) {
				throw new Error('Failed to get predictions');
			}

			return {
				isok: true,
				data: await response.json(),
			};
		} catch (error: any) {
			return {
				isok: false,
				error: error?.message,
			};
		}
	}

	/**
	 * Get current (real-time) predictions from infinno's api
	 * @returns Promise\<InfinnoChatStatusResponse\>
	 */
	static async getSnapshot(): Promise<ShellyReqWrapper<PredictionsRes>> {
		const cacheStr = localStorage.getItem(SNAPSHOT_CACHE_KEY);

		if (cacheStr) {
			try {
				const cachedData = JSON.parse(cacheStr);
				if (Date.now() - cachedData.timestamp < 60000) {
					return {
						isok: true,
						data: cachedData.data,
					};
				}
			} catch (e) {}
		}

		const url = `${this.url}/get-current-state`;
		const request = new Request(url, {
			method: 'GET',
			headers: this.getHeaders(),
			cache: 'no-cache',
		});

		try {
			const response = await fetch(request);

			if (response.status !== 200) {
				throw new Error('Failed to get current predictions');
			}

			const data = await response.json();
			localStorage.setItem(
				SNAPSHOT_CACHE_KEY,
				JSON.stringify({
					timestamp: Date.now(),
					data,
				}),
			);

			return {
				isok: true,
				data,
			};
		} catch (error: any) {
			return {
				isok: false,
				error: error?.message,
			};
		}
	}

	/**
	 * Get all dates that have predictions available
	 * @returns Promise with an array of date strings (YYYY-MM-DD)
	 */
	static async getDatesWithPredictions(): Promise<ShellyReqWrapper<string[]>> {
		const url = `${this.url}/dates-with-preds`;
		const request = new Request(url, {
			method: 'GET',
			headers: this.getHeaders(),
			cache: 'no-cache',
		});

		try {
			const response = await fetch(request);

			if (response.status !== 200) {
				throw new Error('Failed to get dates with predictions');
			}

			return {
				isok: true,
				data: await response.json(),
			};
		} catch (error: any) {
			return {
				isok: false,
				error: error?.message,
			};
		}
	}
}
